import { isIOS } from '@src/utils';

export const SOUNDS = {
  alert: 'alert.wav',
  ban: 'ban.wav',
  click: 'click.wav',
  delete: 'delete.wav',
  download: 'download.wav',
  eject: 'eject.wav',
  joined: 'joined.wav',
  left: 'left.wav',
  lobby: 'lobby.wav',
  locked: 'locked.wav',
  message: 'message.wav',
  notify: 'notify.wav',
  open: 'open.wav',
  raiseHand: 'raiseHand.mp3',
  recStart: 'recStart.wav',
  recStop: 'recStop.wav',
  ring: 'ring.wav',
  roomActive: 'roomActive.wav',
  roomDisactive: 'roomDisactive.wav',
  snapshot: 'snapshot.wav',
  switch: 'switch.wav',
  transcript: 'transcript.wav',
};

export type SoundName = keyof typeof SOUNDS;

// Audio context and buffer cache for iOS compatibility
let audioContext: AudioContext | null = null;
let audioBuffers: Map<string, AudioBuffer> = new Map();
let isAudioContextUnlocked = false;

// Unlock audio context on first user interaction
const unlockAudioContext = async () => {
  if (isAudioContextUnlocked || !audioContext) return;

  try {
    // Resume audio context if suspended (iOS requirement)
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    // Play silent sound to unlock audio
    const buffer = audioContext.createBuffer(1, 1, 22050);
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(audioContext.destination);
    source.start(0);

    isAudioContextUnlocked = true;
  } catch (err) {
    console.warn('Failed to unlock audio context:', err);
  }
};

// Initialize audio context
const initAudioContext = () => {
  if (audioContext) return audioContext;

  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    audioContext = new AudioContextClass();

    // Try to unlock immediately if context is already running
    if (audioContext.state === 'running') {
      isAudioContextUnlocked = true;
    }

    // Listen for user interactions to unlock audio
    const unlockOnInteraction = () => {
      unlockAudioContext();
      // Remove listeners after first unlock
      document.removeEventListener('click', unlockOnInteraction);
      document.removeEventListener('touchstart', unlockOnInteraction);
    };

    document.addEventListener('click', unlockOnInteraction, { once: true });
    document.addEventListener('touchstart', unlockOnInteraction, { once: true });

    // Also try to unlock on any user interaction
    const handleUserInteraction = async () => {
      await unlockAudioContext();
    };

    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);
  } catch (err) {
    console.warn('Failed to initialize audio context:', err);
  }

  return audioContext;
};

// Load audio buffer
const loadAudioBuffer = async (url: string): Promise<AudioBuffer | null> => {
  if (!audioContext) {
    initAudioContext();
    if (!audioContext) return null;
  }

  // Check cache first
  if (audioBuffers.has(url)) {
    return audioBuffers.get(url)!;
  }

  try {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    audioBuffers.set(url, audioBuffer);
    return audioBuffer;
  } catch (err) {
    console.warn(`Failed to load audio: ${url}`, err);
    return null;
  }
};

// Play sound using Web Audio API
const playSoundWithWebAudio = async (buffer: AudioBuffer) => {
  if (!audioContext) {
    initAudioContext();
    if (!audioContext) return false;
  }

  try {
    // Ensure audio context is running
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    const source = audioContext.createBufferSource();
    const gainNode = audioContext.createGain();

    source.buffer = buffer;
    gainNode.gain.value = 0.3;

    source.connect(gainNode);
    gainNode.connect(audioContext.destination);

    source.start(0);
    return true;
  } catch (err) {
    console.warn('Failed to play sound:', err);
    return false;
  }
};

// Fallback to HTML5 Audio for non-iOS or if Web Audio fails
const playSoundWithHTML5 = async (url: string): Promise<boolean> => {
  try {
    const audio = new Audio(url);
    audio.volume = 0.3;
    await audio.play();
    return true;
  } catch (err) {
    return false;
  }
};

export const playSound = async (name: SoundName, isSoundEnabled = false) => {
  if (!isSoundEnabled) return;

  const soundUrl = `../sounds/${SOUNDS[name]}`;
  if (!isIOS()) {
    await playSoundWithHTML5(soundUrl);
    return;
  }

  // Initialize audio context on first call
  if (!audioContext) {
    initAudioContext();
  }

  // Try to unlock audio context
  await unlockAudioContext();

  // Use Web Audio API for iOS, fallback to HTML5 for others
  if (audioContext) {
    const buffer = await loadAudioBuffer(soundUrl);
    if (buffer) {
      const success = await playSoundWithWebAudio(buffer);
      if (success) return;
    }
  }
};
